import { type FC } from 'react'
import { NavLink } from 'react-router-dom'
import { type AccountSchedulingSettings, type UserSchedulingSettings, type AvailabilitySchedule, LocationKind } from '../../../../models/Scheduling'
import { useAppSelector } from '../../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../../store/auth/authSlice'
import {
  useCreateUserSchedulingLocationMutation,
  useDeleteUserSchedulingLocationMutation,
  useGetUserSchedulingLocationsQuery,
  useUpdateUserSchedulingLocationMutation,
} from '../../../../store/scheduling/scheduling'
import { useUserTimezone } from '../../../../common/useUserTimezone'
import { TrackEvent, useTracking } from '../../../../apps/professional-app/fa/common/tracking'
import { Tooltip } from '../../../../components/Tooltip/Tooltip'
import { canManageOwnLocations } from '../../../Scheduling/flows/form/scheduleHelpers'
import { LocationsEditor } from '../../../Scheduling/shared/LocationsEditor'
import { DefaultAvailabilityEditor } from '../../../Scheduling/shared/DefaultAvailabilityEditor'
import { formatTimezoneLabel } from '../../../Scheduling/shared/schedulingDisplay'
import { SettingsCard, SettingsRow, SettingsSection, SettingsToggle } from '../../../Setup/Scheduling/sections/SettingsSection'
import { type UpdateUserSettings } from '../SchedulingProfileTab'
import { AvailabilityCalendarBanner } from './AvailabilityCalendarBanner'
import { ResetToAccountDefaults } from './ResetToAccountDefaults'

interface SectionProps {
  userSettings: UserSchedulingSettings
  accountSettings: AccountSchedulingSettings
  isSaving?: boolean
  onUpdate: UpdateUserSettings
  onReset: () => void
}

export const YourDefaultsSection: FC<SectionProps> = ({ userSettings, accountSettings, isSaving = false, onUpdate, onReset }) => {
  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)
  const accountId = authenticatedUser.accountId
  const userId = authenticatedUser.id
  const { tracked } = useTracking()
  const timezone = useUserTimezone()
  const { data: locations, isFetching: isFetchingLocations, isLoading: isLoadingLocations } = useGetUserSchedulingLocationsQuery({ accountId, userId })
  const [createLocation, { isLoading: isCreatingLocation }] = useCreateUserSchedulingLocationMutation()
  const [updateLocation, { isLoading: isUpdatingLocation }] = useUpdateUserSchedulingLocationMutation()
  const [deleteLocation, { isLoading: isDeletingLocation }] = useDeleteUserSchedulingLocationMutation()
  const isSavingLocations = isCreatingLocation || isUpdatingLocation || isDeletingLocation || (isFetchingLocations && !isLoadingLocations)

  const canManageLocations = canManageOwnLocations(accountSettings.userControls)

  const userOwnedLocations = (locations ?? []).filter((location) => location.ownerUserId === userId)
  const accountLocations = (locations ?? []).filter((location) => !location.ownerUserId)

  const availability: AvailabilitySchedule = userSettings.defaults.availabilitySchedule ?? accountSettings.defaults.availabilitySchedule

  return (
    <SettingsSection title='Availability' subtitle='Set your defaults.' isSaving={isSaving || isSavingLocations}>
      <SettingsCard>
        <div className='pt-3 pb-4 px-4'>
          <LocationsEditor
            locations={userOwnedLocations}
            readOnly={!canManageLocations}
            description='Set up your office locations to offer in-person meetings to your clients.'
            defaultLocationId={userSettings.defaults.locationId}
            defaultLabel='Your default'
            defaultKind={LocationKind.USER_DEFAULT}
            accountLocations={accountLocations}
            accountDefaultLocationId={accountSettings.defaults.locationId}
            onCreate={tracked((input) => void createLocation({ accountId, userId, input }), {
              eventName: TrackEvent.SCHEDULING_LOCATION_CREATED,
              eventDetails: { scope: 'user' },
            })}
            onUpdate={tracked(
              (locationId, input) => void updateLocation({ accountId, userId, locationId, input }),
              {
                eventName: TrackEvent.SCHEDULING_LOCATION_UPDATED,
                eventDetails: { scope: 'user' },
              },
              { eventDetailsExtractor: (locationId) => ({ locationId }) },
            )}
            onDelete={tracked(
              (locationId) => void deleteLocation({ accountId, userId, locationId }),
              {
                eventName: TrackEvent.SCHEDULING_LOCATION_DELETED,
                eventDetails: { scope: 'user' },
              },
              { eventDetailsExtractor: (locationId) => ({ locationId }) },
            )}
            onSetDefault={tracked(
              (locationId) => onUpdate({ defaults: { ...userSettings.defaults, locationId } }),
              {
                eventName: TrackEvent.SCHEDULING_LOCATION_SET_AS_DEFAULT,
                eventDetails: { scope: 'user' },
              },
              { eventDetailsExtractor: (locationId) => ({ locationId }) },
            )}
          />
        </div>
      </SettingsCard>
      <SettingsCard className='zocks-bg-color-gray-100'>
        <SettingsRow className='d-flex align-items-center justify-content-between gap-3'>
          <div className='d-flex flex-column flex-grow-1'>
            <h6 className='mb-0 zocks-text-gray-900'>Timezone</h6>
            <span className='paragraph-small zocks-text-gray-700'>
              Your account default timezone is managed under your <NavLink to='/account/profile-settings/profile-settings'>personal settings</NavLink>. Your
              default availability is interpreted in this timezone.
            </span>
          </div>
          <span className='paragraph-small zocks-text-gray-700 text-nowrap'>{formatTimezoneLabel(timezone)}</span>
        </SettingsRow>
      </SettingsCard>
      <SettingsCard>
        <SettingsRow>
          <SettingsToggle
            label='Federal holidays'
            description="Clients can't book you on US federal holidays or the weekdays they're observed on. Applies to all your booking pages."
            checked={userSettings.defaults.excludeHolidays ?? accountSettings.defaults.excludeHolidays}
            ariaLabel='Exclude federal holidays'
            labelClassName='h6 mb-0 zocks-text-gray-900'
            onChange={(excludeHolidays) => onUpdate({ defaults: { ...userSettings.defaults, excludeHolidays } })}
          />
        </SettingsRow>
      </SettingsCard>
      <SettingsCard>
        <SettingsRow className='d-flex flex-column gap-3'>
          <div className='d-flex flex-column'>
            <span className='d-flex align-items-center zocks-gap-0.25'>
              <h6 className='mb-0 zocks-text-gray-900'>Weekly hours</h6>
              <Tooltip title='Buffers configured on each event type extend beyond these hours.'>
                <i className='bi bi-info-circle zocks-text-gray-500' />
              </Tooltip>
            </span>
            <span className='paragraph-small zocks-text-gray-600'>
              Set your default weekly availability for meetings. Buffers extend beyond these hours, before and after each slot.
            </span>
          </div>
          <div className='d-flex flex-column gap-2'>
            <AvailabilityCalendarBanner integrations={userSettings.integrations} />
            <DefaultAvailabilityEditor
              availability={availability}
              onAvailabilityChange={(availabilitySchedule) => onUpdate({ defaults: { ...userSettings.defaults, availabilitySchedule } })}
            />
          </div>
        </SettingsRow>
      </SettingsCard>
      <ResetToAccountDefaults
        title='Reset to account defaults'
        subtitle="Replace your availability settings with the account defaults. You'll lose any changes you've made here."
        confirmBody="This replaces your availability settings with the account defaults. Any changes you've made here will be lost."
        onReset={onReset}
      />
    </SettingsSection>
  )
}
